import { ChangeDetectionStrategy, Component, computed, input, linkedSignal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { TranslatePipe } from '@ngx-translate/core';
import { CONSTANTS } from '@shared/constants';
import { I18nAvailableLanguage } from '@shared/interfaces/i18n.interface';
import { StatementGroup } from '@shared/interfaces/statements.interface';

interface PreviewRow {
  categoryUuid: string;
  label: string;
  isNew: boolean;
  /** True when the new question has no text yet in the preview language. */
  isEmpty: boolean;
  /** Which empty-state message to show: before any text, vs. missing translation. */
  emptyKey:
    | 'SHARED.FOODBACK_STATEMENTS.MAIN.ADD_QUESTION_MODAL.PREVIEW.EMPTY_QUESTION'
    | 'SHARED.FOODBACK_STATEMENTS.MAIN.ADD_QUESTION_MODAL.PREVIEW.EMPTY_TRANSLATION'
    | null;
}

/**
 * The guest scale, low → high. In the survey the faces are brand-coloured and
 * outlined until the guest picks one; the preview shows the unselected (outlined)
 * state. Maps to assets/svg/smileys/<name>.svg.
 */
const SCALE_FACES = ['sad', '2', '3', '4', '5', 'happy'] as const;

/**
 * Read-only "what the guest sees" preview for the add-question dialog: the scale
 * introduction, the overall-experience row, and one question per category (the
 * new question shown in its category with a "New" badge). Purely illustrative —
 * it renders sampled existing questions and never calls the backend.
 */
@Component({
  selector: 'app-question-guest-preview',
  templateUrl: './question-guest-preview.component.html',
  styleUrl: './question-guest-preview.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MatIconModule, MatMenuModule, TranslatePipe],
})
export class QuestionGuestPreviewComponent {
  /** All statement groups for the account, including the overall-experience group. */
  readonly groups = input.required<StatementGroup[]>();
  readonly selectedCategoryUuid = input<string | null>(null);
  /** The in-progress question keyed by language code (default + translations). */
  readonly newQuestionByLang = input.required<Record<string, string>>();
  readonly defaultLanguageCode = input.required<string>();
  /** Active languages (including the default) offered in the preview language switcher. */
  readonly activeLanguages = input.required<I18nAvailableLanguage[]>();
  /** Scale-introduction stem per language code (e.g. "How satisfied are you with…"). */
  readonly stemByCode = input.required<Record<string, string>>();

  protected readonly faces = SCALE_FACES;

  /** The language the preview is rendered in; defaults to the account default, user-switchable. */
  protected readonly previewLang = linkedSignal<string>(() => this.defaultLanguageCode());

  protected readonly stem = computed(() => this.stemByCode()[this.previewLang()] ?? '');

  protected readonly previewLanguageName = computed(() => {
    const language = this.activeLanguages().find(
      candidate => candidate.code === this.previewLang()
    );

    return language ? this.languageName(language) : this.previewLang();
  });

  protected readonly overallLabel = computed<string | null>(() => {
    const overall = this.groups().find(group => this.isOverallGroup(group.groupName));
    if (!overall?.statements?.length) {
      return null;
    }

    return this.questionText(overall);
  });

  protected readonly rows = computed<PreviewRow[]>(() => {
    const previewLang = this.previewLang();
    const selectedUuid = this.selectedCategoryUuid();

    return this.groups()
      .filter(group => !this.isOverallGroup(group.groupName))
      .map((group): PreviewRow => {
        if (group.groupUuid === selectedUuid) {
          const text = (this.newQuestionByLang()[previewLang] ?? '').trim();
          return {
            categoryUuid: group.groupUuid,
            label: text,
            isNew: true,
            isEmpty: !text,
            emptyKey: text
              ? null
              : previewLang === this.defaultLanguageCode()
                ? 'SHARED.FOODBACK_STATEMENTS.MAIN.ADD_QUESTION_MODAL.PREVIEW.EMPTY_QUESTION'
                : 'SHARED.FOODBACK_STATEMENTS.MAIN.ADD_QUESTION_MODAL.PREVIEW.EMPTY_TRANSLATION',
          };
        }

        return {
          categoryUuid: group.groupUuid,
          label: this.questionText(group),
          isNew: false,
          isEmpty: false,
          emptyKey: null,
        };
      });
  });

  protected selectLanguage(code: string): void {
    this.previewLang.set(code);
  }

  protected languageName(language: I18nAvailableLanguage): string {
    return language.localizedLanguageName ?? language.name ?? language.code;
  }

  private isOverallGroup(groupName: string): boolean {
    return (
      groupName === CONSTANTS.CATEGORIES.NAMES.OVERALL_EXPERIENCE ||
      groupName === CONSTANTS.CATEGORIES.NAMES.TAKEAWAY_OVERALL_EXPERIENCE
    );
  }

  /** A category's sample question text in the preview language, default as fallback. */
  private questionText(group: StatementGroup): string {
    const statements = group.statements ?? [];
    if (statements.length === 0) {
      return '';
    }

    const statement = statements[0];
    const translations = statement.translations ?? {};

    // `flexibleName` carries the category's display name, so it only stands in when the
    // sampled statement has no text to show at all.
    return (
      translations[this.previewLang()] ||
      translations[this.defaultLanguageCode()] ||
      statement.flexibleName ||
      ''
    );
  }
}
